import { useTranslation } from 'react-i18next';
import ReactMarkdown from 'react-markdown';
import { useParams } from 'react-router-dom';

import { Alert, Box, Paper, Typography } from '@mui/material';

import { Loading } from '@/components';
import { LanguageSwitcher } from '@/features/languageSwitcher/LanguageSwitcher';
import { isBorderCrossingRoRo, isBorderCrossingRoRoEmptyV2 } from '@/features/shared/helpers/typologies';

import { useGetFolderAvailableTypologiesAsync } from './apis';
import { PreFolderQualificationWithRORO } from './withRORO';
import {
  stylesLanguageBar,
  stylesErrorMessageBox,
  stylesErrorMessagesText,
  stylesErrorMessagesTitle,
  stylesPreFolderQualification,
  stylesPreFolderQualificationPage,
  stylesPreFolderQualificationText,
  stylesPreFolderQualificationWarning,
} from './withRORO/PreFolderQualificationWithRORO.styles';
import { PreFolderQualificationWithoutRORO } from './withoutRORO';

export const PreFolderQualificationSwitcher = () => {
  const { t } = useTranslation('easy_prefolder');

  const { folderId } = useParams();

  const {
    data: prefolderAvailableTypologies = [],
    isError: isGetPrefolderAvailableTypologiesError,
    isLoading: isGetPrefolderAvailableTypologiesLoading,
  } = useGetFolderAvailableTypologiesAsync({ folderId });

  if (isGetPrefolderAvailableTypologiesLoading) {
    return (
      <Wrapper>
        <Loading />
      </Wrapper>
    );
  }
  if (isGetPrefolderAvailableTypologiesError) {
    return (
      <Wrapper>
        <Alert severity='error' sx={stylesErrorMessageBox}>
          <>
            <Typography sx={stylesErrorMessagesTitle}>
              {t('easy.prefolderQualification.createFolder.errorMessage.title')}
            </Typography>
            <Typography sx={stylesErrorMessagesText}>
              <ReactMarkdown>{t('easy.prefolderQualification.createFolder.errorMessage.text')}</ReactMarkdown>
            </Typography>
          </>
        </Alert>
      </Wrapper>
    );
  }
  const folderTypologyId = prefolderAvailableTypologies?.[0]?.folderTypologyId;

  if (!folderTypologyId) {
    return null;
  }

  return (
    <Wrapper>
      {isBorderCrossingRoRo({ folderTypologyId }) || isBorderCrossingRoRoEmptyV2({ folderTypologyId }) ? (
        <PreFolderQualificationWithRORO />
      ) : (
        <PreFolderQualificationWithoutRORO />
      )}
    </Wrapper>
  );
};

const Wrapper: React.FunctionComponent<{ children: React.ReactNode }> = ({ children }) => {
  const { t } = useTranslation('easy_prefolder');

  return (
    <Box sx={stylesPreFolderQualificationPage}>
      <Box sx={stylesLanguageBar}>
        <LanguageSwitcher />
      </Box>
      <Box component={Paper} elevation={4} sx={stylesPreFolderQualification}>
        <Typography variant='h2' sx={{ mb: 4 }}>
          {t('easy.prefolder.borderPassEasy')}
        </Typography>
        <Typography variant='h3' sx={stylesPreFolderQualificationText}>
          {t('easy.prefolder.qualifyDemand')}
        </Typography>
        <Typography component='p' sx={stylesPreFolderQualificationWarning}>
          {t('easy.prefolder.warning')}
        </Typography>
        {children}
      </Box>
    </Box>
  );
};
